import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoginInput } from '@aksape/validators';
import { authApi } from '../api/auth.api';
import { useAuthStore } from '~/shared/stores/auth.store';

export const AUTH_KEYS = {
  me: ['auth', 'me'] as const,
};

/**
 * Login mutation.
 * On success, updates the auth store with tokens and user data.
 */
export function useLogin() {
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input.email, input.senha),
    onSuccess: (data) => {
      // 2FA opt-in: quando o usuário tem fator TOTP ativo, /auth/login
      // devolve um desafio em vez da sessão — não persistir nada no
      // authStore ainda, a tela de login decide navegar pro desafio.
      if ('mfaRequired' in data) return;

      setAuth({
        user: data.user,
        tenant: data.tenant,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: new Date(data.expiresAt),
      });
    },
  });
}

/**
 * Logout mutation.
 * On success, clears the auth store and query cache.
 */
export function useLogout() {
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      clearAuth();
      queryClient.clear();
    },
  });
}

/**
 * Fetches the current authenticated user.
 * Only runs when the user is authenticated.
 */
export function useCurrentUser() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: AUTH_KEYS.me,
    queryFn: authApi.getMe,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}
